import { useState, useEffect } from 'react';
import { Sparkles, Check, Clock, Users, Shield, Zap, Leaf, PenTool, Loader2 } from 'lucide-react';
import { useOnboardingStore } from '../../../store/onboardingStore';
// import { SalesAngle } from '../../../types/onboarding';
import { axiosInstanceConversationalEngine } from '../../../services/instance';
import { useAuth } from '../../../context/AuthContext';
import { dictionary } from '../../../utils/dictionary';
import { IconCredit, IconCredit1 } from '../../../assets/icons/icons';

export function StepVisualContent() {
  const { language } = useAuth();
  const { data, updateVisualContent, setSalesAngles, salesAngles, hasFetchedSalesAngles, setHasFetchedSalesAngles, updateContextData } = useOnboardingStore();
  const { visualContent, productData } = data;
  const [isCustomAngle, setIsCustomAngle] = useState(visualContent.selectedAngle.some((angle) => angle.name === 'custom'));
  const [isLoading, setIsLoading] = useState(!hasFetchedSalesAngles);

  useEffect(() => {
    const fetchSalesAngles = async () => {
      // If we've already fetched angles, don't fetch again
      if (hasFetchedSalesAngles) {
        setIsLoading(false);
        return;
      }

      setIsLoading(true);
      try {
        const response = await axiosInstanceConversationalEngine.post('/resolve-info-funnel', {
          product_name: productData?.name || '',
          product_description: productData?.details || '',
        });

        // const response = {
        //   data: {
        //     sales_angles: [
        //       {
        //         name: 'Custom Angle',
        //         description: 'Create your own approach.',
        //         isRecommended: false,
        //       },
        //       {
        //         name: 'Custom Angle 2',
        //         description: 'Create your own approach 2.',
        //         isRecommended: true,
        //       },
        //     ],
        //     pain_detection: 'Pain detection 2',
        //     buyer_detection: 'Buyer detection 2',
        //   }
        // };


        if (response.data && Array.isArray(response.data.sales_angles) && response.data.sales_angles.length > 0) {
          // Map API response (sales_angles) to our SalesAngle format
          const apiSalesAngles = response.data.sales_angles.map((angle: any, index: number) => {
            // Assign icons based on index or use default ones
            const iconMap = [Clock, Users, Shield, Zap, Leaf];
            // Generate ID from name
            const id = angle.name ? angle.name.toLowerCase().replace(/\s+/g, '-') : `angle-${index}`;

            return {
              id,
              title: angle.name || `Angle ${index + 1}`,
              description: angle.description || 'No description available',
              icon: iconMap[index % iconMap.length],
              isRecommended: index === 0 // Make first one recommended
            };
          });

          updateContextData({
            pain_detection: response.data.pain_detection || '',
            buyer_detection: response.data.buyer_detection || '',
            recommended_sales_angles: response.data.sales_angles || [],
          });

          // Always keep the custom angle option
          const customAngle = {
            id: 'custom',
            title: dictionary["custom-angle"][language],
            description: dictionary["create-your-own-approach"][language],
            icon: PenTool
          };

          const finalAngles = [...apiSalesAngles, customAngle];
          setSalesAngles(finalAngles);

          // If no angle is selected, select the first one
          if (!visualContent.selectedAngle) {
            updateVisualContent({
              selectedAngle: apiSalesAngles[0].id
            });
          }
        }
        // else {
        //   setSalesAngles(defaultSalesAngles);
        // }
      } catch (error) {
        console.error("Error fetching sales angles:", error);
        updateContextData({
          pain_detection: 'Error fetching data.',
          buyer_detection: 'Error fetching data.',
          recommended_sales_angles: [],
        });
      } finally {
        setIsLoading(false);
        setHasFetchedSalesAngles(true);
      }
    };

    fetchSalesAngles();
  }, [productData?.name, productData?.details, hasFetchedSalesAngles, visualContent.selectedAngle, updateVisualContent, setSalesAngles, setHasFetchedSalesAngles, updateContextData]);

  // quiero que esta funcion haga push y sea un array

  const handleSelectAngle = (angleId: string, angleDescription: string) => {
    if (visualContent.selectedAngle.some((angle) => angle.name === angleId)) {
      updateVisualContent({
        selectedAngle: visualContent.selectedAngle.filter((angle) => angle.name !== angleId)
      });
      return;
    }
    if (visualContent.selectedAngle.length === 6) {
      return;
    }
    if (angleId === 'custom') {
      setIsCustomAngle(true);
      updateVisualContent({
        selectedAngle: [...visualContent.selectedAngle, { name: angleId, description: angleDescription }],
        customAngle: visualContent.customAngle || ''
      });
    } else {
      setIsCustomAngle(false);
      updateVisualContent({
        selectedAngle: [...visualContent.selectedAngle, { name: angleId, description: angleDescription }],
        customAngle: undefined
      });
    }
  };

  const handleCustomAngleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    updateVisualContent({
      customAngle: e.target.value
    });
  };

  return (
    <div className="space-y-6">
      <h3 className="text-xl font-medium mb-4">
        {dictionary["choose-1-to-6-sales-angles-for-your-funnels"][language]}
        <p className="text-sm text-gray-500 mt-2">
          {dictionary["each-angle-selected-1-credit"][language]}
        </p>
      </h3>

      {/* Sales Angles Grid */}
      {isLoading ? (
        <div className="flex justify-center items-center p-12">
          <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
          <span className="ml-3 text-gray-600">{dictionary["loading-personalized-sales-angles"][language]}</span>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {salesAngles.map((angle) => {
            const IconComponent = angle.icon;
            const isCustom = angle.id === 'custom';

            return (
              <div
                key={angle.id}
                className={`relative border rounded-lg p-5 cursor-pointer transition-all ${isCustom ? 'border-dashed border-gray-300' : ''
                  } ${visualContent.selectedAngle.some((selectedAngle) => selectedAngle.name === angle.id)
                    ? 'border-primary-600 ring-1 ring-primary-600 bg-primary-50'
                    : 'border-gray-200 hover:border-gray-300'
                  }`}
                onClick={() => handleSelectAngle(angle.id, angle.description)}
              >
                {/* Badge en esquina superior derecha */}
                <div className="absolute top-[-10px] right-[-10px] bg-gray-200 text-black text-xs font-medium px-3 py-0.5 flex items-center rounded-[12px]">
                  {/* <YourIcon className="w-3 h-3 mr-1" /> aquí metes tu ícono */}
                  <IconCredit1 width="10px" height="10px" color="#000000" />
                  <p style={{ marginLeft: '4px', fontSize: '10px' }}>
                    {dictionary["1-credit"][language]}
                  </p>
                </div>

                <div className="flex justify-between items-start mb-3">
                  <div className="flex items-center">
                    <div
                      className={`p-2 rounded-full ${visualContent.selectedAngle.some(
                        (selectedAngle) => selectedAngle.name === angle.id
                      )
                        ? 'bg-primary-100'
                        : 'bg-gray-100'
                        }`}
                    >
                      {IconComponent && (
                        <IconComponent
                          className={`w-5 h-5 ${visualContent.selectedAngle.some(
                            (selectedAngle) => selectedAngle.name === angle.id
                          )
                            ? 'text-primary-600'
                            : 'text-gray-600'
                            }`}
                        />
                      )}
                    </div>
                    <h4 className="font-medium text-lg ml-2">{angle.title}</h4>
                  </div>
                  {visualContent.selectedAngle.some(
                    (selectedAngle) => selectedAngle.name === angle.id
                  ) && <Check className="w-5 h-5 text-primary-600" />}
                </div>

                <p className="text-gray-600 text-sm">{angle.description}</p>

                {angle.isRecommended && (
                  <div className="mt-3">
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                      <Sparkles className="w-3 h-3 mr-1" />
                      {dictionary['recommended'][language]}
                    </span>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Custom Angle Form */}
      {isCustomAngle && (
        <div className="border-dashed border-2 border-primary-200 rounded-lg bg-primary-50 p-6">
          <label htmlFor="custom-angle" className="block text-sm font-medium text-gray-700 mb-2">
            {dictionary["describe-your-custom-sales-angle"][language]}
          </label>
          <textarea
            id="custom-angle"
            className="textarea h-32 w-full"
            placeholder={dictionary["describe-your-custom-sales-angle-placeholder"][language]}
            value={visualContent.customAngle || ''}
            onChange={handleCustomAngleChange}
          />
          <p className="mt-2 text-sm text-gray-500">
            {dictionary["describe-your-custom-sales-angle-footer"][language]}
          </p>
        </div>
      )}
    </div>
  );
}